import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useQuery, useInfiniteQuery } from "react-query";
import {
  getMonthWiseStats,
  getDayWiseStats,
} from "../../services/analyticsService";
import { getCommonProducts } from "../../services/commonProductService";
import { formatAmount } from "../../utils/formatters";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { subMonths, startOfMonth, subDays } from "date-fns";
import { useNavigate, useLocation } from "react-router-dom";

// All known portals (posting accounts) - show every portal even when count/revenue is 0
const ALL_PORTALS = [
  "amazon",
  "flipkart",
  "shopify",
  "tata 1mg",
  "palsons derma pvt ltd",
];

const toLocalMidnight = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getInitialStartDate = (timeRange, endDate = new Date()) => {
  if (timeRange === "month") {
    return toLocalMidnight(
      new Date(endDate.getFullYear(), endDate.getMonth() - 2, 1) // 3 months (current month + 2 previous months)
    );
  } else {
    const d = new Date(endDate);
    d.setDate(d.getDate() - 90); // 3 months (90 days)
    return toLocalMidnight(d);
  }
};

const PortalWiseSalesTrend = () => {
  const location = useLocation();
  const STORAGE_KEY = "filters:portal-wise";
  let savedFilters = null;
  try {
    savedFilters = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch (_) {}
  const TODAY_KEY = new Date().toLocaleDateString("en-CA");
  if (savedFilters && savedFilters.dateKey !== TODAY_KEY) {
    savedFilters = null;
  }
  const navigate = useNavigate();

  // Initialize timeRange first (no hourly - only month or day)
  const rawInitialTimeRange = location.state?.timeRange || savedFilters?.timeRange || "month";
  const initialTimeRange = rawInitialTimeRange === "hour" ? "day" : rawInitialTimeRange;

  const [timeRange, setTimeRange] = useState(initialTimeRange);
  const [endDate, setEndDate] = useState(() => {
    if (location.state?.endDate) {
      return toLocalMidnight(new Date(location.state.endDate));
    }
    if (savedFilters?.endDate) {
      return toLocalMidnight(new Date(savedFilters.endDate));
    }
    return toLocalMidnight(new Date());
  });
  const [startDate, setStartDate] = useState(() => {
    if (location.state?.startDate) {
      return toLocalMidnight(new Date(location.state.startDate));
    }
    if (savedFilters?.startDate) {
      return toLocalMidnight(new Date(savedFilters.startDate));
    }
    return toLocalMidnight(getInitialStartDate(initialTimeRange, new Date()));
  });
  const [zone, setZone] = useState(
    location.state?.zone || savedFilters?.zone || ""
  );
  const [productName, setProductName] = useState(
    location.state?.productName || savedFilters?.productName || ""
  );
  const [productBaseSku, setProductBaseSku] = useState(
    location.state?.productBaseSku ?? savedFilters?.productBaseSku ?? ""
  );
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const productDropdownRef = useRef(null);
  const [showQuantity, setShowQuantity] = useState(
    location.state?.showQuantity ?? savedFilters?.showQuantity ?? false
  );
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc"); // "asc" or "desc"
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          dateKey: TODAY_KEY,
          startDate: startDate?.toISOString?.() || startDate,
          endDate: endDate?.toISOString?.() || endDate,
          timeRange,
          zone,
          productName,
          productBaseSku,
          showQuantity,
        })
      );
    } catch (_) {}
  }, [startDate, endDate, timeRange, zone, productName, productBaseSku, showQuantity]);

  // Track previous timeRange to detect when switching
  const prevTimeRangeRef = useRef(timeRange);
  
  // Adjust dates when switching time ranges
  useEffect(() => {
    // Only adjust when switching TO a different time range
    if (prevTimeRangeRef.current === timeRange) {
      prevTimeRangeRef.current = timeRange;
      if (toLocalMidnight(endDate) < startDate) {
        const newStartDate = getInitialStartDate(timeRange, endDate);
        setStartDate(toLocalMidnight(newStartDate));
      }
      return;
    }

    if (timeRange === "month" && endDate) {
      const endDateMonth = endDate.getMonth();
      const endDateYear = endDate.getFullYear();

      // Always set start date to 1st of the month when in monthly view
      const firstDayOfMonth = new Date(endDateYear, endDateMonth, 1);
      setStartDate(toLocalMidnight(firstDayOfMonth));

      // Set end date to last day of the month
      const lastDayOfMonth = new Date(endDateYear, endDateMonth + 1, 0);
      setEndDate(toLocalMidnight(lastDayOfMonth));
    }
    
    // Update the ref for next comparison
    prevTimeRangeRef.current = timeRange;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, endDate]);

  const handleStartDateChange = (date) => {
    if (timeRange === "month") {
      const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
      setStartDate(toLocalMidnight(firstDay));
    } else {
      setStartDate(toLocalMidnight(date));
    }
  };

  const handleEndDateChange = (date) => {
    if (timeRange === "month") {
      const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      setEndDate(toLocalMidnight(lastDay));
      const newStartDate = new Date(date.getFullYear(), date.getMonth() - 2, 1);
      if (toLocalMidnight(date) < startDate) {
        setStartDate(toLocalMidnight(newStartDate));
      }
    } else {
      setEndDate(toLocalMidnight(date));
      if (toLocalMidnight(date) < startDate) {
        setStartDate(getInitialStartDate(timeRange, date));
      }
    }
  };

  // Product options: searchable dropdown via common products API (same as SalesComparison)
  const searchQueryForApi = productSearchQuery.trim();
  const PRODUCT_PAGE_SIZE = 10;
  const {
    data: productOptionsInfiniteData,
    isLoading: productOptionsLoading,
    isFetchingNextPage,
    hasNextPage: productOptionsHasNextPage,
    fetchNextPage: fetchNextProductPage,
  } = useInfiniteQuery(
    ["commonProducts-search-portal", searchQueryForApi],
    ({ pageParam = 1 }) =>
      getCommonProducts({
        page: pageParam,
        limit: PRODUCT_PAGE_SIZE,
        sortField: "name",
        sortValue: "ASC",
        searchKey: searchQueryForApi,
      }),
    {
      enabled: productSearchOpen,
      getNextPageParam: (lastPage, allPages) =>
        lastPage?.data?.hasNext ? (allPages?.length ?? 0) + 1 : undefined,
      staleTime: 60 * 1000,
    }
  );
  const productOptionsFromApi = useMemo(() => {
    const pages = productOptionsInfiniteData?.pages ?? [];
    return pages.flatMap((page) => {
      const list = Array.isArray(page?.data?.data) ? page.data.data : [];
      return list
        .filter((p) => p.baseSku)
        .map((p) => ({ product: p.name || "", sku: p.baseSku || "" }));
    });
  }, [productOptionsInfiniteData?.pages]);
  const productOptions = useMemo(
    () => [{ product: "All Products", sku: "" }, ...productOptionsFromApi],
    [productOptionsFromApi]
  );
  const selectedProductLabel = useMemo(
    () => productName || "All Products",
    [productName]
  );

  const {
    data: salesData,
    isLoading: isSalesLoading,
    error: salesError,
  } = useQuery(
    ["salesData", timeRange, startDate, endDate, zone, productBaseSku || productName],
    () => {
      // Format dates based on timeRange
      let formattedStartDate, formattedEndDate;
      
      // Helper function to format date as YYYY-MM-DD
      const formatDateToYYYYMMDD = (date) => {
        if (!date) {
          console.error("Date is null or undefined:", date);
          return null;
        }
        
        // Ensure we have a Date object
        let dateObj = date;
        if (!(date instanceof Date)) {
          dateObj = new Date(date);
        }
        
        if (isNaN(dateObj.getTime())) {
          console.error("Invalid date:", date, dateObj);
          return null;
        }
        
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const formatted = `${year}-${month}-${day}`;
        
        // Validate the result
        if (!/^\d{4}-\d{2}-\d{2}$/.test(formatted)) {
          console.error("Date formatting failed:", { date, dateObj, formatted });
          return null;
        }
        
        return formatted;
      };
      
      // For monthly and daily, ensure YYYY-MM-DD format
      formattedStartDate = formatDateToYYYYMMDD(startDate);
      formattedEndDate = formatDateToYYYYMMDD(endDate);

      // Validate dates before proceeding
      if (!formattedStartDate || !formattedEndDate) {
        console.error("Date formatting failed:", { startDate, endDate });
        throw new Error("Invalid date format");
      }

      formattedStartDate = formattedStartDate.split('T')[0];
      formattedEndDate = formattedEndDate.split('T')[0];

      // Validate format matches YYYY-MM-DD
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(formattedStartDate) || !dateRegex.test(formattedEndDate)) {
        console.error("Date format validation failed:", { formattedStartDate, formattedEndDate });
        throw new Error(`Invalid date format. Expected YYYY-MM-DD, got: ${formattedStartDate}, ${formattedEndDate}`);
      }

      const params = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        statType: "postingAccount", // Portal-wise stats use postingAccount statType
        groupBy: "day",
        ...(zone && { zone }),
        ...(productBaseSku && productBaseSku.trim()
          ? { baseSku: productBaseSku.trim() }
          : productName && { productName }),
      };
      
      // For monthly view use month-wise stats; for daily view use day-wise stats
      return timeRange === "month"
        ? getMonthWiseStats(params)
        : getDayWiseStats(params);
    },
    {
      onError: (error) => {
        console.log("Sales Data Error:", error);
        toast.error(error.message || "Failed to load sales data");
      },
    }
  );

  // Merge API data with all portals so every portal is shown (with 0 when no data)
  const salesDataWithAllPortals = useMemo(() => {
    if (!salesData || !Array.isArray(salesData)) return salesData;

    const grandTotalRow = salesData.find(
      (s) =>
        (s.postingAccount || s.stat_value || "")
          .toLowerCase()
          .includes("total") ||
        (s.postingAccount || s.stat_value || "")
          .toLowerCase()
          .includes("grand")
    );
    const portalRows = salesData.filter(
      (s) =>
        (s.postingAccount || s.stat_value || "")
          .toLowerCase()
          .includes("total") === false &&
        (s.postingAccount || s.stat_value || "")
          .toLowerCase()
          .includes("grand") === false
    );

    if (timeRange === "month") {
      const monthNames = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
      ];
      const zeroCell = { net_amount: 0, qty: 0 };
      const byPortal = new Map(
        portalRows.map((r) => [r.postingAccount || r.stat_value, r])
      );
      const merged = ALL_PORTALS.map((portal) => {
        if (byPortal.has(portal)) return byPortal.get(portal);
        return {
          postingAccount: portal,
          ...Object.fromEntries(
            monthNames.map((m) => [m, { ...zeroCell }])
          ),
          total: { ...zeroCell },
        };
      });
      if (grandTotalRow) merged.push(grandTotalRow);
      return merged;
    }

    // Day view
    let dateKeys = [];
    if (portalRows.length > 0 && portalRows[0].daily_data) {
      dateKeys = Object.keys(portalRows[0].daily_data).sort(
        (a, b) => new Date(a) - new Date(b)
      );
    } else {
      const cur = new Date(startDate);
      const end = new Date(endDate);
      while (cur <= end) {
        dateKeys.push(cur.toISOString().split("T")[0]);
        cur.setDate(cur.getDate() + 1);
      }
    }
    const zeroCell = { net_amount: 0, qty: 0 };
    const byPortal = new Map(
      portalRows.map((r) => [r.stat_value || r.postingAccount, r])
    );
    const merged = ALL_PORTALS.map((portal) => {
      if (byPortal.has(portal)) return byPortal.get(portal);
      return {
        stat_value: portal,
        daily_data: Object.fromEntries(
          dateKeys.map((d) => [d, { ...zeroCell }])
        ),
        total_net_amount: 0,
        total_qty: 0,
      };
    });
    if (grandTotalRow) merged.push(grandTotalRow);
    return merged;
  }, [salesData, timeRange, startDate, endDate]);

  const formatCell = (data) => {
    if (!data) return showQuantity ? "0" : "₹0";
    return showQuantity
      ? `${data.qty}`
      : `${formatAmount(data.net_amount ?? 0)}`;
  };

  const resetFilters = () => {
    const today = toLocalMidnight(new Date());
    setTimeRange("month");
    setEndDate(today);
    setStartDate(getInitialStartDate("month", today));
    setZone("");
    setProductName("");
    setProductBaseSku("");
    setProductSearchOpen(false);
    setProductSearchQuery("");
  };

  // Export to CSV function (aligned with ProductAnalytics: loading state + try/finally)
  const handleExportToCSV = async () => {
    setIsExporting(true);
    try {
      const productParam =
        productBaseSku && productBaseSku.trim()
          ? { baseSku: productBaseSku.trim() }
          : productName ? { productName } : {};
      const params = new URLSearchParams(
        timeRange === "month"
          ? {
              startMonth: startDate.toLocaleDateString("en-CA").split("T")[0],
              endMonth: endDate.toLocaleDateString("en-CA").split("T")[0],
              statType: "postingAccount",
              qtyReq: showQuantity,
              exportToCsv: "true",
              ...(zone && { zone }),
              ...productParam,
            }
          : {
              startMonth: startDate.toLocaleDateString("en-CA").split("T")[0],
              endMonth: endDate.toLocaleDateString("en-CA").split("T")[0],
              statType: "postingAccount",
              groupBy: "day",
              qtyReq: showQuantity,
              exportToCsv: "true",
              ...(zone && { zone }),
              ...productParam,
            }
      );

      const baseURL =
        process.env.REACT_APP_API_BASE_URL ||
        "https://digitalapi.palsonsderma.com";
      const response = await fetch(
        `${baseURL}/data/order/${
          timeRange === "month" ? "month-wise-stats" : "day-wise-stats"
        }?${params.toString()}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      const contentDisposition = response.headers.get("Content-Disposition");
      const match = contentDisposition?.match(/filename[*]?=(?:UTF-8'')?"?([^";\n]+)"?/i);
      const filename =
        match?.[1]?.trim().replace(/^["']|["']$/g, "")
          ? decodeURIComponent(match[1].trim().replace(/^["']|["']$/g, ""))
          : `portal-wise-sales-${startDate
              .toLocaleDateString("en-CA")
              .split("T")[0]}-to-${endDate
              .toLocaleDateString("en-CA")
              .split("T")[0]}${zone ? `-${zone}` : ""}${productBaseSku && productBaseSku.trim() ? `-${productBaseSku.trim()}` : productName ? `-${productName}` : ""}.xlsx`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download export");
    } finally {
      setIsExporting(false);
    }
  };

  // Prepare data for charts (use merged data so all portals appear even with 0)
  const prepareChartData = () => {
    if (!salesDataWithAllPortals) return { barData: [], pieData: [] };

    // Filter out totals and get only portal data
    const portalData = salesDataWithAllPortals.filter((sale) => {
      const name = sale.postingAccount || sale.stat_value;
      return name && !name.toLowerCase().includes("total");
    });

    // Calculate totals for both amount and quantity
    const totalAmount = portalData.reduce((sum, sale) => {
      let amount = 0;
      if (timeRange === "month") {
        amount = sale.total?.net_amount || 0;
      } else {
        amount = sale.total_net_amount || 0;
      }
      return sum + amount;
    }, 0);

    const totalQuantity = portalData.reduce((sum, sale) => {
      let qty = 0;
      if (timeRange === "month") {
        qty = sale.total?.qty || 0;
      } else {
        qty = sale.total_qty || 0;
      }
      return sum + qty;
    }, 0);

    // Bar chart data (always amount)
    const barData = portalData.map((sale) => {
      let amount = 0;
      if (timeRange === "month") {
        amount = sale.total?.net_amount || 0;
      } else {
        amount = sale.total_net_amount || 0;
      }

      const percentage =
        totalAmount > 0 ? ((amount / totalAmount) * 100).toFixed(1) : 0;

      return {
        name: sale.postingAccount || sale.stat_value,
        value: amount,
        percentage: parseFloat(percentage),
        formattedValue: formatAmount(amount),
      };
    });

    // Pie chart data (always quantity)
    const pieData = portalData.map((sale) => {
      let qty = 0;
      if (timeRange === "month") {
        qty = sale.total?.qty || 0;
      } else {
        qty = sale.total_qty || 0;
      }

      const percentage =
        totalQuantity > 0 ? ((qty / totalQuantity) * 100).toFixed(1) : 0;

      return {
        name: sale.postingAccount || sale.stat_value,
        value: qty,
        percentage: parseFloat(percentage),
        formattedValue: qty.toString(),
      };
    });

    return {
      barData,
      pieData,
    };
  };

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];

  const RADIAN = Math.PI / 180;
  const renderPieLabel = ({ cx, cy, midAngle, outerRadius, value }) => {
    if (value == null) return null;
    const label = `${value} sales`;
    const radius = outerRadius * 0.7;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const labelWidth = label.length * 7 + 8;
    const labelHeight = 14;
    const rectX = x - labelWidth / 2;
    const rectY = y - labelHeight / 2;
    return (
      <g>
        <rect
          x={rectX}
          y={rectY}
          width={labelWidth}
          height={labelHeight}
          rx={3}
          ry={3}
          fill="rgba(255,255,255,0.9)"
          stroke="#e5e7eb"
        />
        <text
          x={x}
          y={rectY + labelHeight - 4}
          fill="#374151"
          fontSize={10}
          textAnchor="middle"
        >
          {label}
        </text>
      </g>
    );
  };

  const renderBarChart = () => {
    const { barData } = prepareChartData();

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">
          Portal Wise Sales Revenue
        </h3>
        <div className="relative overflow-visible ">
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={barData}
              margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "#6b7280" }}
                angle={-25}
                textAnchor="end"
                interval={0}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 14, fill: "#6b7280" }}
                tickFormatter={(value) => formatAmount(value)}
              />
              <Tooltip
                formatter={(value, name) => [
                  formatAmount(value),
                  "Amount",
                ]}
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  maxWidth: 280,
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                }}
                wrapperStyle={{ maxWidth: 280 }}
                labelStyle={{
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                }}
                cursor={{ fill: "rgba(59, 130, 246, 0.1)" }}
              />
              <Legend />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={40}>
                {barData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={`hsl(${200 + index * 30}, 70%, ${50 + index * 5}%)`}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderPieChart = () => {
    const { pieData } = prepareChartData();

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">
          Portal Wise Sales Distribution
        </h3>
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderPieLabel}
              outerRadius={120}
              fill="#8884d8"
              dataKey="value"
            >
              {pieData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name, props) => [
                `${value} sales`,
                props.payload.name || "Portal",
              ]}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New field, start with ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <span className="inline-block ml-1 text-gray-400">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M6 2L9 6H3L6 2Z" />
            <path d="M6 10L3 6H9L6 10Z" opacity="0.4" />
          </svg>
        </span>
      );
    }

    if (sortDirection === "asc") {
      return (
        <span className="inline-block ml-1 text-blue-600">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M6 2L9 6H3L6 2Z" />
          </svg>
        </span>
      );
    } else {
      return (
        <span className="inline-block ml-1 text-blue-600">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M6 10L3 6H9L6 10Z" />
          </svg>
        </span>
      );
    }
  };

  const sortData = (data, field, direction) => {
    if (!field) return data;

    // Separate grand total rows from regular data
    const grandTotalRows = data.filter((item) => {
      const name = (item.postingAccount || item.stat_value || "").toLowerCase();
      return name.includes("total") || name.includes("grand");
    });

    const regularData = data.filter((item) => {
      const name = (item.postingAccount || item.stat_value || "").toLowerCase();
      return !(name.includes("total") || name.includes("grand"));
    });

    // Sort only the regular data
    const sortedRegularData = [...regularData].sort((a, b) => {
      let aVal, bVal;

      if (field === "marketplace") {
        // For marketplace/portal name column
        aVal = (a.postingAccount || a.stat_value || "").toLowerCase();
        bVal = (b.postingAccount || b.stat_value || "").toLowerCase();
      } else if (field === "total") {
        if (timeRange === "month") {
          aVal = showQuantity ? a.total?.qty || 0 : a.total?.net_amount || 0;
          bVal = showQuantity ? b.total?.qty || 0 : b.total?.net_amount || 0;
        } else {
          aVal = showQuantity ? a.total_qty || 0 : a.total_net_amount || 0;
          bVal = showQuantity ? b.total_qty || 0 : b.total_net_amount || 0;
        }
      } else {
        // For month/date columns
        if (timeRange === "month") {
          aVal = showQuantity ? a[field]?.qty || 0 : a[field]?.net_amount || 0;
          bVal = showQuantity ? b[field]?.qty || 0 : b[field]?.net_amount || 0;
        } else {
          const aTimeData = a.daily_data;
          const bTimeData = b.daily_data;
          aVal = showQuantity
            ? aTimeData?.[field]?.qty || 0
            : aTimeData?.[field]?.net_amount || 0;
          bVal = showQuantity
            ? bTimeData?.[field]?.qty || 0
            : bTimeData?.[field]?.net_amount || 0;
        }
      }

      if (direction === "asc") {
        if (typeof aVal === "string") return aVal.localeCompare(bVal);
        return aVal - bVal;
      } else {
        if (typeof aVal === "string") return bVal.localeCompare(aVal);
        return bVal - aVal;
      }
    });

    // Combine sorted regular data with grand total rows at the end
    return [...sortedRegularData, ...grandTotalRows];
  };

  const renderTable = () => {
    if (!salesDataWithAllPortals) return null;
    const sales = sortData(salesDataWithAllPortals, sortField, sortDirection);
    if (timeRange === "month") {
      // Generate monthList between startDate and endDate
      const monthList = [];
      let current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
      while (current <= end) {
        monthList.push({
          key: `${current.getFullYear()}-${current.getMonth() + 1}`,
          label: current.toLocaleString("default", { month: "short" }),
          year: current.getFullYear(),
          month: current.getMonth(),
        });
        current = new Date(current.getFullYear(), current.getMonth() + 1, 1);
      }
      return (
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort("marketplace")}
                >
                  <div className="flex items-center">
                    Marketplace
                    {getSortIcon("marketplace")}
                  </div>
                </th>
                {monthList.map((m) => (
                  <th
                    key={m.key}
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort(m.label)}
                  >
                    <div className="flex items-center">
                      {m.label} {m.year}
                      {getSortIcon(m.label)}
                    </div>
                  </th>
                ))}
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort("total")}
                >
                  <div className="flex items-center">
                    Total
                    {getSortIcon("total")}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sales.map((sale, index) => {
                const isGrandTotal =
                  sale.postingAccount?.toLowerCase().includes("total") ||
                  sale.postingAccount?.toLowerCase().includes("grand");
                return (
                  <tr
                    key={index}
                    className={isGrandTotal ? "font-bold" : ""}
                    style={
                      isGrandTotal
                        ? { backgroundColor: "#e0f2fe", color: "#0c4a6e" }
                        : {}
                    }
                  >
                    <td
                      className="whitespace-nowrap capitalize transition-colors duration-150 cursor-pointer hover:bg-blue-100 hover:text-blue-700"
                      style={{ cursor: isGrandTotal ? undefined : "pointer" }}
                      onClick={() => {
                        if (sale.postingAccount && !isGrandTotal) {
                          const params = new URLSearchParams({
                            platform: sale.postingAccount,
                            startDate: startDate
                              .toLocaleDateString("en-CA")
                              .split("T")[0],
                            endDate: endDate
                              .toLocaleDateString("en-CA")
                              .split("T")[0],
                          });
                          navigate(
                            `/analytics/product-by-state?${params.toString()}`,
                            {
                              state: {
                                fromPage: "/analytics/portal-wise-sales-trend",
                                startDate:
                                  startDate.toLocaleDateString("en-CA"),
                                endDate: endDate.toLocaleDateString("en-CA"),
                                timeRange: timeRange,
                                zone: zone,
                                showQuantity: showQuantity,
                              },
                            }
                          );
                        }
                      }}
                    >
                      {sale.postingAccount}
                    </td>
                    {monthList.map((m) => {
                      const key = m.label;
                      return <td key={m.key}>{formatCell(sale[key])}</td>;
                    })}
                    <td>{formatCell(sale.total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    } else {
      // For daily view
      const dates = Object.keys(sales[0]?.daily_data || {})
        .sort((a, b) => new Date(a) - new Date(b))
        .map((date) => {
          const d = new Date(date);
          return {
            fullDate: date,
            displayDate: `${d.getDate()}-${d.toLocaleString("default", {
              month: "short",
            })}`,
          };
        });
      return (
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort("marketplace")}
                >
                  <div className="flex items-center">
                    Marketplace
                    {getSortIcon("marketplace")}
                  </div>
                </th>
                {dates.map(({ fullDate, displayDate }) => (
                  <th
                    key={fullDate}
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort(fullDate)}
                  >
                    <div className="flex items-center">
                      {displayDate}
                      {getSortIcon(fullDate)}
                    </div>
                  </th>
                ))}
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort("total")}
                >
                  <div className="flex items-center">
                    Total
                    {getSortIcon("total")}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sales.map((sale, index) => {
                const isGrandTotal =
                  sale.stat_value?.toLowerCase().includes("total") ||
                  sale.stat_value?.toLowerCase().includes("grand");
                return (
                  <tr
                    key={index}
                    className={isGrandTotal ? "font-bold" : ""}
                    style={
                      isGrandTotal
                        ? { backgroundColor: "#e0f2fe", color: "#0c4a6e" }
                        : {}
                    }
                  >
                    <td
                      className="whitespace-nowrap capitalize transition-colors duration-150 cursor-pointer hover:bg-blue-100 hover:text-blue-700"
                      style={{ cursor: isGrandTotal ? undefined : "pointer" }}
                      onClick={() => {
                        if (sale.stat_value && !isGrandTotal) {
                          const params = new URLSearchParams();
                          params.append("platform", sale.stat_value);
                          params.append(
                            "startDate",
                            startDate.toLocaleDateString("en-CA").split("T")[0]
                          );
                          params.append(
                            "endDate",
                            endDate.toLocaleDateString("en-CA").split("T")[0]
                          );
                          navigate(
                            `/analytics/product-by-state?${params.toString()}`,
                            {
                              state: {
                                fromPage: "/analytics/portal-wise-sales-trend",
                                startDate:
                                  startDate.toLocaleDateString("en-CA"),
                                endDate: endDate.toLocaleDateString("en-CA"),
                                timeRange: timeRange,
                                zone: zone,
                                showQuantity: showQuantity,
                              },
                            }
                          );
                        }
                      }}
                    >
                      {sale.stat_value}
                    </td>
                    {dates.map(({ fullDate }) => (
                      <td key={`${sale.stat_value}-${fullDate}`}>
                        {formatCell(sale.daily_data?.[fullDate])}
                      </td>
                    ))}
                    <td>
                      {showQuantity
                        ? sale.total_qty
                        : formatAmount(sale.total_net_amount ?? 0)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
  };

  if (isSalesLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <h1 className="text-2xl font-bold">Portal Wise Sales Trend</h1>
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center w-full">
          <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center w-full lg:w-auto">
            <DatePicker
              selected={startDate}
              onChange={handleStartDateChange}
              className="text-sm input input-bordered w-full sm:w-40"
              dateFormat={timeRange === "month" ? "yyyy-MM" : "yyyy-MM-dd"}
              showMonthYearPicker={timeRange === "month"}
              showFullMonthYearPicker={timeRange === "month"}
              minDate={
                timeRange === "month"
                  ? new Date(endDate.getFullYear(), endDate.getMonth() - 11, 1)
                  : new Date(endDate.getTime() - 365 * 24 * 60 * 60 * 1000)
              }
              maxDate={endDate}
            />
            <DatePicker
              selected={endDate}
              onChange={handleEndDateChange}
              className="text-sm input input-bordered w-full sm:w-40"
              dateFormat={timeRange === "month" ? "yyyy-MM" : "yyyy-MM-dd"}
              showMonthYearPicker={timeRange === "month"}
              showFullMonthYearPicker={timeRange === "month"}
              maxDate={new Date()}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center w-full lg:w-auto">
            <div className={`relative w-full sm:w-48 lg:w-56 ${productSearchOpen ? "z-[10001]" : ""}`}>
             
              <div ref={productDropdownRef} className="relative">
                <input
                  type="text"
                  value={productSearchOpen ? productSearchQuery : selectedProductLabel}
                  onChange={(e) => {
                    setProductSearchQuery(e.target.value);
                    setProductSearchOpen(true);
                  }}
                  onFocus={() => {
                    setProductSearchOpen(true);
                    setProductSearchQuery("");
                  }}
                  onClick={() => {
                    setProductSearchOpen(true);
                    setProductSearchQuery("");
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      if (productDropdownRef.current && !productDropdownRef.current.contains(document.activeElement)) {
                        setProductSearchOpen(false);
                      }
                    }, 200);
                  }}
                  placeholder="Search product or SKU..."
                  className="input input-bordered w-full pr-8 text-sm"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-sm">
                  {productSearchOpen ? "⌃" : "⌄"}
                </span>
                {productSearchOpen && (
                  <ul
                    className="absolute z-[10001] mt-1 w-full max-h-60 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg py-1 text-sm"
                    onScroll={(e) => {
                      const el = e.currentTarget;
                      if (!productOptionsHasNextPage || isFetchingNextPage) return;
                      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
                      if (nearBottom) fetchNextProductPage();
                    }}
                  >
                    {productOptionsLoading && productOptionsFromApi.length === 0 ? (
                      <li className="px-3 py-2 text-gray-500">{productSearchQuery.trim() ? "Searching..." : "Loading..."}</li>
                    ) : productOptions.length <= 1 ? (
                      <li className="px-3 py-2 text-gray-500">{productSearchQuery.trim() ? "No matches" : "Type to search or scroll for more"}</li>
                    ) : (
                      <>
                        {productOptions.map((opt) => (
                          <li
                            key={opt.sku ? `sku-${opt.sku}` : "all"}
                            className="px-3 py-2 cursor-pointer hover:bg-gray-100 flex flex-col"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setProductName(opt.product && opt.sku ? opt.product : "");
                              setProductBaseSku(opt.sku ? opt.sku : "");
                              setProductSearchQuery("");
                              setProductSearchOpen(false);
                            }}
                          >
                            <span className="font-medium truncate" title={opt.product}>
                              {opt.product || "All Products"}
                            </span>
                            {opt.sku && (
                              <span className="text-xs text-gray-500 truncate">SKU: {opt.sku}</span>
                            )}
                          </li>
                        ))}
                        {productOptionsHasNextPage && (
                          <li className="px-3 py-2 text-gray-500 text-center border-t border-gray-100">
                            {isFetchingNextPage ? "Loading more..." : "Scroll for more"}
                          </li>
                        )}
                      </>
                    )}
                  </ul>
                )}
              </div>
              
            </div>

            <div className="btn-group">
              <button
                className={`btn btn-sm ${
                  timeRange === "day" ? "btn-primary" : "btn-ghost"
                }`}
                onClick={() => setTimeRange("day")}
              >
                Daily
              </button>
              <button
                className={`btn btn-sm ${
                  timeRange === "month" ? "btn-primary" : "btn-ghost"
                }`}
                onClick={() => setTimeRange("month")}
              >
                Monthly
              </button>
            </div>

            <button
              type="button"
              className="btn btn-ghost btn-sm border border-gray-300"
              onClick={resetFilters}
            >
              <svg className="w-4 h-4 mr-1.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Reset filters
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleExportToCSV}
              disabled={isSalesLoading || isExporting}
            >
              {isSalesLoading || isExporting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                "Export"
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Charts Grid - Side by side on desktop, stacked on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {renderBarChart()}
        {renderPieChart()}
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">
            Portal Wise {showQuantity ? "Sales Count" : "Sales Revenue"}
            </h3>
            <div className="flex items-center space-x-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="toggle toggle-primary"
                  checked={showQuantity}
                  onChange={(e) => setShowQuantity(e.target.checked)}
                />
                <span className="text-sm font-medium">Show Sales Count</span>
              </label>
            </div>
          </div>
          <div className="overflow-x-auto">{renderTable()}</div>
        </div>
      </div>
    </div>
  );
};

export default PortalWiseSalesTrend;
